import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Drawer, Loader } from "rsuite";
import { OrderCreationProvider, useOrderCreation } from "./context/OrderCreationContext";
import { SetFlightInfo } from "./steps/SetFlightInfo";
import { AddressSelection } from "./steps/AddressSelection";
import { RideRequirements } from "./steps/RideRequirements";
import { LuggageSelection } from "./steps/LuggageSelection";
import { TripSettings } from "./steps/TripSettings";
import { OrderConfirmation } from "./steps/OrderConfirmation";
import { PassengerInfo } from "./steps/PassengerInfo";
import { OrderComplete } from "./steps/OrderComplete";
import Header from "../../../../layouts/Header";
import AddressSelect from "../../../../components/AddressSelect";
import RideTimeSelect from "../../../../components/RideTimeSelec";
import PageBase from "../../../../components/PageBase";
import { AirportDrawer } from "./components/AirportDrawer";
import { getTerminals } from "../../../../service/AppSettingService";
import { getLuggageTypes, checkDateType, getAllTeamVehicles, TeamVehicle } from "../../../../service/VehicleService";
import { authUserData } from "../../../../service";

const terminals = getTerminals().filter((terminal) => terminal.code !== 'TPE');

const OrderCreationWizardContent: React.FC = () => {
  const navigate = useNavigate();
  const authUser = authUserData();

  const {
    step,
    setStep,
    headerTitle,
    setHeaderTitle,
    isShowLoading,
    setIsShowLoading,
    openAirportSelect,
    setOpenAirportSelect,
    setSelectedTerminal,
    rideTime,
    setRideTime,
    setRideDateType,
    setSelectedPlace,
    setSelectedAddress,
    luggageTypes,
    setLuggageTypes,
    userRegistLuggages,
    setUserRegistLuggages,
    vehicleTypes,
    setVehicleTypes,
    passengerEmail,
    setPassengerEmail,
    refLuggageNote,
    luggageNote
  } = useOrderCreation();

  useEffect(() => {
    const updateHeaderTitle = () => {
      switch (step) {
        case 0:
          setHeaderTitle("航班資訊");
          break;
        case 1:
          setHeaderTitle("接送地址");
          break;
        case 2:
          setHeaderTitle("搭乘需求");
          break;
        case 3:
          setHeaderTitle("行李數量");
          if (refLuggageNote.current) {
            refLuggageNote.current.value = luggageNote;
          }
          break;
        case 4:
          setHeaderTitle("設定行程");
          break;
        case 5:
          setHeaderTitle("確認訂單");
          break;
        case 6:
          setHeaderTitle("旅客資訊");
          break;
        case 7:
          setHeaderTitle("搜尋地址");
          break;
        case 9:
          setHeaderTitle("預約完成");
          break;
        default:
          break;
      }
    };

    const initializeLuggageTypes = async () => {
      if (luggageTypes.length === 0) {
        let types = await getLuggageTypes();
        if (types && types.length > 0) {
          setLuggageTypes(types);

          if (userRegistLuggages.length === 0) {
            const initialLuggages = types.map(element => ({
              luggage: element,
              quantity: 0
            }));
            setUserRegistLuggages(initialLuggages);
          }
        }
      }
    };

    const initializeVehicleTypes = async () => {
      if (vehicleTypes.length === 0 && authUser?.team?.id) {
        try {
          const vehicleList = await getAllTeamVehicles(authUser.team.id);
          if (vehicleList) {
            // Map TeamVehicle to Vehicle format and filter enabled/active
            const enabledVehicleTypes = vehicleList
              .filter((v: TeamVehicle) => v.enabled && v.isActive)
              .map((v: TeamVehicle) => ({
                id: v.id,
                vehicleID: v.vehicleID,
                vehiclename: v.vehicleName,
                peopleLimit: v.peopleLimit,
                totalLuggageLimit: v.totalLuggageLimit,
                luggageSupporting: v.luggageSupporting.map(l => ({
                  luggage: {
                    size: l.luggageSize,
                    discription: l.luggageDescription || `${l.luggageSize}吋`
                  },
                  quantity: l.quantity
                })),
                enabled: v.enabled
              }));
            setVehicleTypes(enabledVehicleTypes);
          }
        } catch (error) {
          console.error('Error fetching vehicles:', error);
        }
      }
    };

    const initializePassengerEmail = () => {
      if (passengerEmail === undefined && authUser?.email && authUser.email.length > 0) {
        setPassengerEmail(authUser.email);
      }
    };

    updateHeaderTitle();
    initializeLuggageTypes();
    initializeVehicleTypes();
    initializePassengerEmail();
  }, [step, authUser, luggageTypes.length, vehicleTypes.length, passengerEmail,
    setHeaderTitle, setLuggageTypes, setUserRegistLuggages, setVehicleTypes,
    setPassengerEmail, userRegistLuggages.length, refLuggageNote, luggageNote]);

  const doNavigate = () => {
    switch (step) {
      case 0:
        navigate("/rider");
        break;
      case 1:
        setStep(0);
        break;
      case 2:
        setStep(1);
        break;
      case 3:
        setStep(2);
        break;
      case 4:
        setStep(2);
        break;
      case 5:
        setStep(4);
        break;
      case 6:
        setStep(5);
        break;
      case 7:
        setStep(1);
        break;
      case 8:
        setStep(1);
        break;
      case 9:
        navigate("/");
        break;
      default:
        break;
    }
  };

  const innerStyle = () => {
    if (step >= 0) {
      return { backgroundColor: 'white' };
    }
    return {};
  };

  const pageHeader = (
    <Header
      HeaderTitle={headerTitle}
      isShowRefresh={false}
      OnReturn={doNavigate}
      OnRefresh={() => { }}
    />
  );

  return (
    <div className="App" style={innerStyle()}>
      {isShowLoading && <Loader content="請稍候..." />}

      {step === 0 && (
        <PageBase header={pageHeader} content={<SetFlightInfo />} showSide />
      )}

      {step === 1 && (
        <PageBase header={pageHeader} content={<AddressSelection />} showSide />
      )}

      {step === 2 && (
        <PageBase header={pageHeader} content={<RideRequirements />} showSide />
      )}

      {step === 3 && (
        <PageBase header={pageHeader} content={<LuggageSelection />} showSide />
      )}

      {step === 4 && (
        <PageBase header={pageHeader} content={<TripSettings />} showSide />
      )}

      {step === 5 && (
        <PageBase header={pageHeader} content={<OrderConfirmation />} showSide />
      )}

      {step === 6 && (
        <PageBase header={pageHeader} content={<PassengerInfo />} showSide />
      )}

      {step === 7 && (
        <PageBase
          header={pageHeader}
          content={
            <AddressSelect
              onPlaceSelect={(value) => {
                setSelectedPlace(value!);
                setSelectedAddress(value?.address);
                setStep(1);
              }}
            />
          }
          showSide
        />
      )}

      {step === 9 && (
        <PageBase header={pageHeader} content={<OrderComplete />} showSide />
      )}

      <AirportDrawer
        open={openAirportSelect}
        terminals={terminals}
        onClose={() => setOpenAirportSelect(false)}
        onSelect={(terminal) => setSelectedTerminal(terminal)}
      />

      <Drawer size="xs" backdrop placement="left" open={step === 8} onClose={() => setStep(1)}>
        {isShowLoading && <Loader content="正在檢查日期是否為假日，請稍候..." />}
        <Drawer.Header>
          <Drawer.Title>選擇時間</Drawer.Title>
        </Drawer.Header>
        <RideTimeSelect
          defaultDate={rideTime}
          onDatetimeSelect={async (selectedDateTime) => {
            if (selectedDateTime) {
              setRideTime(selectedDateTime);
              setIsShowLoading(true);
              let varRideDateType = await checkDateType(selectedDateTime);
              setRideDateType(varRideDateType);
              setIsShowLoading(false);
              setStep(1);
            }
          }}
        />
      </Drawer>

      <p></p>
    </div>
  );
};

const OrderCreationWizard: React.FC = () => {
  return (
    <OrderCreationProvider>
      <OrderCreationWizardContent />
    </OrderCreationProvider>
  );
};

export default OrderCreationWizard;
